import React, { useEffect, useRef, useState } from 'react';
import { useT, LANGS } from '../i18n';
import './LanguagePopover.css';

// ── THE LANGUAGE GEAR AND ITS DROPDOWN ───────────────────────────────────────
// The small menu that opens from the gear beside the sign-up button: a label
// and three pills, the same shape the signed-in user menu already uses for the
// same job. Deliberately NOT the full-screen picker — that one exists for a
// first-time visitor who has never chosen and needs to be stopped and asked.
// Changing a language you have already set is a two-second correction, and
// blacking out the page for it is a heavier gesture than the act deserves.
//
// The component owns its trigger as well as its menu, because the two have to
// share the open state and an outside-click that knows about both. Callers pass
// `triggerClassName` so each page can size and colour the button to its own
// header without this file knowing anything about either.
//
// `children` are rendered ABOVE the language block, and exist for one reason:
// on a phone the landing's four nav links do not fit beside the auth buttons,
// so they are hidden there — and this gear is the only surface left that can
// hold them. The menu closes when one is followed, which is why the caller's
// links are wrapped in a click handler here rather than each knowing about the
// menu's state.
export default function LanguagePopover({ triggerClassName = '', children }) {
  const { t, lang, setLang } = useT();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Closes on a click anywhere else and on Escape. Bound at the document, and
  // only while open — a listener that lives for the life of the page to serve
  // a menu that is shut is work for nothing.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // `capture`, so the gear's own click cannot immediately reopen what this
    // just closed.
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="lgp-wrap" ref={wrapRef}>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => setOpen(v => !v)}
        // A gear promises settings. Once this menu also holds the site's
        // navigation it is not a settings button any more, so the name follows
        // what is behind it — see the icons below.
        aria-label={children ? t('nav_menu') : t('lang_settings')}
        title={children ? t('nav_menu') : t('lang_settings')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {/* ── TWO ICONS, ONE SHOWN ────────────────────────────────────────
            A gear where the menu holds only the language, three lines where it
            also holds the navigation — which on the landing is a question of
            viewport, since the nav links inside are themselves hidden on a
            desktop. CSS decides, because the answer is the same breakpoint
            that hides the header's own link row, and a `matchMedia` here could
            disagree with it. Both are inert marks; only one is ever painted. */}
        <svg className="lgp-ico lgp-ico--gear" viewBox="0 0 24 24" width="17" height="17"
          fill="none" stroke="currentColor"
          strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3.1" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
        </svg>
        <svg className="lgp-ico lgp-ico--menu" viewBox="0 0 24 24" width="19" height="19"
          fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      {open && (
        <div className="lgp-menu" role="menu">
          {/* Whatever the page put here — on the landing, the nav links a phone
              has no room for. `onClick` on the wrapper rather than on each
              link: they are the caller's elements and this menu should not
              require them to know it exists. */}
          {children && (
            <div className="lgp-extra" onClick={() => setOpen(false)}>
              {children}
            </div>
          )}
          <div className="lgp-label">{t('menu_language')}</div>
          <div className="lgp-pills">
            {LANGS.map(l => (
              <button
                key={l.code}
                type="button"
                role="menuitemradio"
                aria-checked={lang === l.code}
                className={`lgp-pill${lang === l.code ? ' lgp-pill--on' : ''}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
                // The short code is what the pill shows; the full name is what
                // a screen reader should say, and what a tooltip is for.
                title={l.name}
                aria-label={l.name}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
